import videojs from "video.js";
import xPath from "@utilities/XPath";
import parseTime from "@utilities/ParseTime";
import Cache from "@utilities/Cache";

const logger = videojs.createLogger("AdTriggerVMAP");
const Plugin = videojs.getPlugin("plugin");

// Default options for the plugin.
const defaults = {
	enabled: true,
	vmapOverride: null
};

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class VMAP extends Plugin
{

	/**
   * Create a VMAP plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
	constructor(player, options)
	{
		// The parent class will add player under this.player
		super(player);

		this.options = videojs.mergeOptions(defaults, options);
		this.vmap = null;

		if (!this.options.enabled)
		{
			return;
		}

		if (this.options.vmapOverride)
		{
			// ! If overriding the VMAP URL, a race exists. If the content VMAP
			// ! loads before the Ad VMAP, pre-rolls may fail to play as no
			// ! pre-rolls were seen in time for the content to play (and it
			// ! assumes no pre-rolls exist). This will not break mid-rolls or
			// ! post-rolls, however.
			fetch(this.options.vmapOverride)
				.then(resp => resp.text())
				.then(resp => xPath(resp, "//VMAP").iterateNext())
				.then(this.handleVMAP.bind(this));
		}
		else
		{
			player.on("vmap-ready", () => { this.handleVMAP(player.vmap); });
		}
	}

	handleVMAP(vmap)
	{
		this.vmap = vmap;

		const contentTag = xPath.ext(vmap, "/Content").iterateNext();
		const miscTag = xPath.ext(vmap, "/MiscInfo").iterateNext();

		if (contentTag && !contentTag.textContent)
		{
			// The broadcast is upcoming. Don't play pre-rolls
			return;
		}

		// If we loaded the VMAP via an ad tag override, we may not have a
		// MiscInfo tag
		let timeout = 0;
		if (miscTag)
		{
			// Measured in minutes, convert to milliseconds:
			timeout = miscTag.getAttribute("prerollTimeout") * 60 * 1000;
		}
		const lastPreroll = Cache.get("lastpreroll", 0);

		if ((Date.now() - timeout) < lastPreroll)
		{
			logger(
				"Skipping Prerolls due to prerollTimeout " +
				`(${Date.now()} - ${lastPreroll} < ${timeout})`
			);
		}

		const adBreakTags = xPath(this.vmap, "//AdBreak");

		// No Ads
		if (adBreakTags.length === 0)
			return;

		// Find pre-rolls and post-rolls
		// VMAP mid-rolls will be ignored for now (support to be added later)
		let next = adBreakTags.iterateNext();
		while (next)
		{
			const time = parseTime(next.getAttribute("timeOffset"));
			if (time.roll === "preroll" && (Date.now() - timeout) >= lastPreroll)
			{
				const countTag = xPath(
					next,
					"//Extensions/Extension[@type='VolarAdBreakExtension']/Count"
				).iterateNext();

				let count = 1;
				if (countTag)
				{
					count = parseInt(countTag.textContent);
					logger.debug(`Pre-roll count tag exists. Setting count to ${countTag.textContent}`);
				}
				else
				{
					logger.debug("No pre-roll count tag. Setting count to 1");
				}

				if (isNaN(count))
				{
					logger.debug("Count is NaN (bug?) -- setting count to 1");
					this.addPreroll(next);
				}
				else
				{
					for (var i = 0; i < count; i++)
					{
						logger.debug("Adding pre-roll");
						this.addPreroll(next);
					}
				}
			}
			/*
			if (time.roll === "postroll")
				this.addPostroll(next);
			*/

			next = adBreakTags.iterateNext();
		}
	}

	addPreroll(adBreakTag)
	{
		const adData = VMAP.getAdData(adBreakTag);
		const preloadedAd = this.player.blueframeAds().load(adData.adTagURI, {
			name: "Preroll"
		});
		if (preloadedAd)
		{
			// Record when the pre-roll ends so we don't play a bunch too soon
			preloadedAd.on("ended", () =>
			{
				Cache.set("lastpreroll", Date.now());
			});
			this.player.ads.addPreroll(preloadedAd);
		}
	}

	/*
	addPostroll(adBreakTag)
	{
		const adData = VMAP.getAdData(adBreakTag);
		const preloadedAd = this.player.blueframeAds().load(adData.adTagURI, {
			name: "Postroll"
		});
		// TODO: Maybe a smarter way to pass these back to the ad plugin? Maybe
		if (preloadedAd)
		{
			this.player.ads.addPostroll(preloadedAd);
		}
	}
	*/

	// Copied from Metadata plugin
	// This should be abstracted
	// Maybe an "Ad" utility should be created which can also house parseTime
	static getAdData(adBreak)
	{
		// In order to play the ad we only need to know the ad tag URI
		// Eventually we may add support for other things like ContentSkip or
		// Interval... Maybe. We may decide those are unnecessary
		const adSource = xPath(
			adBreak,
			"//Extensions/Extension[@type='VolarAdBreakExtension']/AdSource|//AdSource"
		).iterateNext();

		return {
			adTagURI: xPath(adSource, "//AdTagURI").iterateNext().textContent
		};
	}
}

// Register the plugin with video.js.
videojs.registerPlugin("adTriggerVMAP", VMAP);

export default VMAP;
